import { useState, useCallback, useRef } from 'react';
import { Button } from '@artstorefronts/ui';
import { Download, Loader } from 'lucide-react';
import clsx from 'clsx';
import { useMutation } from '@apollo/client';

import { useAnalytics } from '../context/analytics';
import { useAuth } from '../context/auth';
import { MARK_MOCKUP_DOWNLOADED } from '../graphql/mutations';

interface DownloadButtonProps {
  imageUrl: string;
  title?: string;
  className?: string;
  imageId?: string | null;
  mockupId?: string | null;
}

function DownloadButton({ imageUrl, title, className, imageId, mockupId }: DownloadButtonProps) {
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState(0);
  const abortControllerRef = useRef<AbortController | null>(null);

  const { amplitude } = useAnalytics();
  const { refetchProfile } = useAuth();
  const [markMockupDownloaded] = useMutation(MARK_MOCKUP_DOWNLOADED);

  const handleDownload = useCallback(
    async (e: any) => {
      e.stopPropagation();

      if (isDownloading || !imageUrl) return;

      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      abortControllerRef.current = new AbortController();

      setIsDownloading(true);
      let downloadSucceeded = false;
      try {
        const response = await fetch(imageUrl, {
          signal: abortControllerRef.current.signal,
          cache: 'no-cache',
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${title || 'image'}.jpeg`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        downloadSucceeded = true;
      } catch (error: any) {
        if (error.name === 'AbortError') {
          console.log('Download cancelled');
        } else {
          console.error('Download failed:', error);
        }
      } finally {
        setIsDownloading(false);
        setDownloadProgress(0);
        abortControllerRef.current = null;
        amplitude.track('mockup_download', {
          image_url: imageUrl,
          image_id: imageId?.toString() || null,
          mockup_id: mockupId || null,
        });

        if (downloadSucceeded) {
          try {
            await markMockupDownloaded({
              variables: { input: { hasMockupDownloads: true } },
            });
            await refetchProfile();
          } catch (error) {
            console.error('Error marking mockup as downloaded:', error);
          }
        }
      }
    },
    [imageUrl, title, isDownloading, amplitude, imageId, mockupId, markMockupDownloaded, refetchProfile]
  );

  return (
    <Button
      variant="ghost"
      className={clsx('bg-black/70 hover:bg-black/90 text-white hover:text-white', className)}
      onClick={handleDownload}
      disabled={isDownloading}
    >
      {isDownloading ? (
        <div className="flex items-center gap-2">
          <Loader className="h-4 w-4 animate-spin" />
          <div className="text-xs">{downloadProgress.toFixed(0)}%</div>
        </div>
      ) : (
        <div className="flex items-center gap-2">
          <Download className="h-4 w-4" />
        </div>
      )}
    </Button>
  );
}

export default DownloadButton;
